import { Link } from 'react-router-dom';

import { Close } from '@mui/icons-material';
import { Box, Typography, styled } from '@mui/material';

import { QuantitySelector } from 'components/shared/QuantitySelector';

import useProducts from 'hooks/products/useProducts';

import type { CartLineItem } from 'models/Cart';

import { formatPrice } from 'utils/numberFormatter';
import { getShopifyID, getNetPrice, getCurrency } from 'utils/utils';
import { useTranslation } from 'react-i18next';

const StyledImage = styled(Box)(() => ({
    width: '90px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    img: {
        maxWidth: '100%',
    },
}));

const StyledTitle = styled(Link)(() => ({
    color: 'black',
    fontWeight: '500',
    '&:hover': {
        textDecoration: 'underline',
    },
}));

const StyledContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    margin: '12px 0',
    padding: '12px',
    border: '1px solid #e6e6e6',
    borderRadius: '5px',
}));

const StyledRow = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
}));

const IconButton = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '50%',
    cursor: 'pointer',
    ':active': {
        backgroundColor: '#e6e6e6',
    },
    ':hover': {
        backgroundColor: '#e6e6e6',
    },
}));

interface MobileProductItemProps {
    item: CartLineItem;
    onDelete: VoidFunction;
    onChangeQuantity: (quantity: number) => void;
    updatingCart: boolean;
}

const MobileProductItem: React.FC<MobileProductItemProps> = ({
    item,
    onDelete,
    onChangeQuantity,
    updatingCart,
}) => {
    const { t } = useTranslation('cart', { keyPrefix: 'CART' });

    const products = useProducts();

    const getPrice = (): number => {
        return Number(
            item.prices.find(
                (elem) =>
                    elem.variantId == Number(getShopifyID(item.merchandise.id))
            ).price
        );
    };

    const getMinQty = (): number => {
        return products.getMinOrderQtyFromMetafields(
            item.merchandise.product.metafields
        );
    };

    const getMultiplier = (): boolean => {
        return products.getMultiplierFromMetafields(
            item.merchandise.product.metafields
        );
    };

    return (
        <StyledContainer>
            <Box>
                <StyledImage>
                    <img
                        src={item.merchandise.product.featuredImage.url}
                        alt={item.merchandise.product.title}
                    />
                </StyledImage>
            </Box>
            <Box
                sx={{
                    flexWrap: 'wrap',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                }}
            >
                <Box>
                    <StyledTitle
                        to={'/products/' + item.merchandise.product.handle}
                    >
                        <Typography variant="h6">
                            {item.merchandise.product.title}
                        </Typography>
                    </StyledTitle>

                    <Typography>{item.merchandise.sku}</Typography>
                </Box>

                <Box>
                    <Typography variant="h6">
                        {t('GROSS_PRICE')}:{' '}
                        {formatPrice(
                            item.prices
                                ? getPrice()
                                : Number(item.cost.amountPerQuantity.amount)
                        )}{' '}
                        {getCurrency(item.cost.amountPerQuantity.currencyCode)}
                    </Typography>

                    <Typography variant="h6">
                        {t('ONLINE_NET_PRICE')}:{' '}
                        {formatPrice(
                            item.prices
                                ? getPrice() * item.quantity
                                : getNetPrice(
                                      Number(item.cost.totalAmount.amount)
                                  )
                        )}{' '}
                        {getCurrency(item.cost.totalAmount.currencyCode)}
                    </Typography>
                </Box>

                <StyledRow>
                    <QuantitySelector
                        defaultQty={Number(item.quantity)}
                        qtyChanged={onChangeQuantity}
                        minOrderQuantity={getMinQty()}
                        minOrderMultiplier={getMultiplier()}
                        disabled={updatingCart}
                    />
                    <IconButton onClick={onDelete}>
                        <Close />
                    </IconButton>
                </StyledRow>
            </Box>
        </StyledContainer>
    );
};

export default MobileProductItem;
